import React, { useEffect, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { Box, Slide, Typography, Card, CardContent, Stack, Button } from '@mui/material';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { Link } from 'react-router-dom';
import { HOST, IsDSM, IsMerchandisingProgram } from 'config/config';
import { DealerPortalSectionEnumValue, getDealerPortalSectionName } from 'enums/DealerPortalSectionEnum';
import { useSelector } from 'react-redux';
import { useDealer } from 'services/Data/DealerServices';
import ImageCarousel from './HomePageCarousel';
import { usePortalBanners } from 'services/Data/BannerServices';
import { BlobStorageTypeEnumValue } from 'enums/BlobStorageTypeEnum';
import { useDealerPortalMenuTree } from 'services/Data/DealerPortalMenuServices';
import { getPodLinkBehavior } from 'services/Utilities/podRoutingUtils';
import { IsClub } from 'config/config';

const showroomProjectRequestLink = '/dealer/home-page/showroom-project-request';

const HomePage = () => {
  const { t, i18n } = useTranslation();
  let hist = useHistory();

  const accountReducer = useSelector(state => state.AccountReducer);
  const { data: dealer } = useDealer();
  const { data: portalBanners = [] } = usePortalBanners();
  const isLoggedIn = Boolean(accountReducer?.isLoggedIn);
  const { data: displaysMenuTreeData } = useDealerPortalMenuTree(
    DealerPortalSectionEnumValue.DisplaysToolingAndFurniture,
    isLoggedIn && !IsMerchandisingProgram,
  );
  const { data: exteriorSignageMenuTreeData } = useDealerPortalMenuTree(
    DealerPortalSectionEnumValue.ExteriorSignage,
    isLoggedIn,
  );
  const { data: merchandisingMenuTreeData } = useDealerPortalMenuTree(
    DealerPortalSectionEnumValue.MerchandisingServices,
    isLoggedIn,
  );
  const { data: printOnDemandMenuTreeData } = useDealerPortalMenuTree(
    DealerPortalSectionEnumValue.PrintOnDemand,
    isLoggedIn,
  );

  const getOrderedChildren = menuNode => {
    if (Array.isArray(menuNode?.children)) {
      return [...menuNode.children].sort((left, right) => Number(left?.order ?? 0) - Number(right?.order ?? 0));
    }

    if (Array.isArray(menuNode?.Children)) {
      return [...menuNode.Children].sort((left, right) => Number(left?.order ?? 0) - Number(right?.order ?? 0));
    }

    return [];
  };

  const getFirstOrderedMenuId = menuNodes => {
    const sortedNodes = [...(Array.isArray(menuNodes) ? menuNodes : [])].sort(
      (left, right) => Number(left?.order ?? 0) - Number(right?.order ?? 0),
    );

    if (sortedNodes.length === 0) {
      return null;
    }

    const firstNode = sortedNodes[0];
    const childFirstMenuId = getFirstOrderedMenuId(getOrderedChildren(firstNode));

    return childFirstMenuId || firstNode?.id;
  };

  const getDefaultMenuId = menuNodes => {
    const sortedNodes = [...(Array.isArray(menuNodes) ? menuNodes : [])].sort(
      (left, right) => Number(left?.order ?? 0) - Number(right?.order ?? 0),
    );

    for (const menuNode of sortedNodes) {
      if (menuNode?.isDefaultHomePageMenu || menuNode?.IsDefaultHomePageMenu) {
        return menuNode?.id;
      }

      const childDefaultMenuId = getDefaultMenuId(getOrderedChildren(menuNode));

      if (childDefaultMenuId) {
        return childDefaultMenuId;
      }
    }

    return null;
  };

  const buildSectionMenuRoute = (sectionValue, menuTreeData) => {
    const sectionName = getDealerPortalSectionName(sectionValue);
    const firstMenuId = getDefaultMenuId(menuTreeData) || getFirstOrderedMenuId(menuTreeData);

    if (!sectionName || !firstMenuId) {
      return '/dealer/home-page';
    }

    return `/dealer/section/${sectionName}/menu/${firstMenuId}?fromHomeMenu=1`;
  };

  const displayLink = useMemo(
    () => buildSectionMenuRoute(DealerPortalSectionEnumValue.DisplaysToolingAndFurniture, displaysMenuTreeData),
    [displaysMenuTreeData],
  );
  const exteriorSignageLink = useMemo(
    () => buildSectionMenuRoute(DealerPortalSectionEnumValue.ExteriorSignage, exteriorSignageMenuTreeData),
    [exteriorSignageMenuTreeData],
  );
  const merchandisingServicesLink = useMemo(
    () => buildSectionMenuRoute(DealerPortalSectionEnumValue.MerchandisingServices, merchandisingMenuTreeData),
    [merchandisingMenuTreeData],
  );
  const printOnDemandLink = useMemo(
    () => buildSectionMenuRoute(DealerPortalSectionEnumValue.PrintOnDemand, printOnDemandMenuTreeData),
    [printOnDemandMenuTreeData],
  );
  const printOnDemandLinkBehavior = useMemo(() => getPodLinkBehavior(printOnDemandLink), [printOnDemandLink]);

  const certificationLink = '/dealer/certification';
  useEffect(() => {
    if (!isLoggedIn || !dealer) {
      return;
    }

    if (dealer.hasPortalAccess === false && !IsDSM && !IsClub) {
      hist.replace(certificationLink);
    }
  }, [dealer, hist, certificationLink, isLoggedIn]);

  const navTiles = useMemo(
    () =>
      [
        !IsMerchandisingProgram && {
          key: 'displays',
          title: t('homeDisplays'),
          description: t('orderNow'),
          image: '/images/headers/DisplaysToolingandFurniture.jpg',
          to: displayLink,
          component: Link,
        },
        !IsClub && {
          key: 'certification',
          title: t('certificationtitle'),
          title2: t('certificationtitle2'),
          description: t('submit'),
          image: '/images/headers/CertificationPhotoTool.jpg',
          to: certificationLink,
          component: Link,
        },
        {
          key: 'signage',
          title: t('homeSignage1'),
          title2: t('homeSignage2'),
          description: t('orderNow'),
          image: '/images/headers/ExteriorSignage.jpg',
          imagePosition: '70% center',
          to: exteriorSignageLink,
          component: Link,
        },
        {
          key: 'merchandising',
          title: t('homeMerchandising'),
          title2: t('homeMerchandising2'),
          description: t('enrollNow'),
          image: '/images/headers/MerchandisingServices.jpg',
          to: merchandisingServicesLink,
          component: Link,
        },
        IsClub && {
          key: 'project-request-form',
          title: t('project-request-form'),
          description: t('requestNow'),
          image: '/images/headers/project-request-form.jpg',
          component: Link,
          to: showroomProjectRequestLink,
        },
        // IsClub &&
        //   !IsMerchandisingProgram && {
        //     key: 'print-on-demand',
        //     title: t('home.printOnDemandTitle'),
        //     description: t('orderNow'),
        //     image: '/images/headers/print-on-demand.png',
        //     component: printOnDemandLinkBehavior.isExternalRoute ? 'a' : Link,
        //     to: printOnDemandLinkBehavior.isExternalRoute ? undefined : printOnDemandLinkBehavior.url,
        //     href: printOnDemandLinkBehavior.isExternalRoute ? printOnDemandLinkBehavior.url : undefined,
        //     target:
        //       printOnDemandLinkBehavior.isExternalRoute && printOnDemandLinkBehavior.openInNewTab ? '_blank' : undefined,
        //     rel: printOnDemandLinkBehavior.isExternalRoute ? 'noopener noreferrer' : undefined,
        //   },
      ].filter(Boolean),
    [t, displayLink, certificationLink, exteriorSignageLink, merchandisingServicesLink, printOnDemandLinkBehavior],
  );

  const bannerSlides = useMemo(
    () =>
      (portalBanners || [])
        .filter(banner => (banner?.useBackgroundColor ? Boolean(banner?.backgroundColor) : Boolean(banner?.fileId)))
        .map(banner => ({
          id: banner.id,
          imgSrc: banner.useBackgroundColor
            ? null
            : `${HOST}/api/dashboard/attached-files/${banner.id}/${banner.fileId}/download?type=${BlobStorageTypeEnumValue.Banner}`,
          useBackgroundColor: banner.useBackgroundColor,
          backgroundColor: banner.backgroundColor,
          title: banner.title,
          titleColor: banner.titleColor,
          subtitle: banner.subtitle,
          subtitleColor: banner.subtitleColor,
          buttonText: banner.buttonText,
          showButton: banner.showButton,
          linkUrl: banner.linkUrl,
          useDealerPortalRoute: banner.useDealerPortalRoute,
          isExternalRoute: banner.isExternalRoute,
        })),
    [portalBanners, i18n.language],
  );

  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'relative',
          minHeight: {
            xs: 'calc(100vh - 140px)',
            sm: 'calc(100vh - 160px)',
            md: 'calc(100vh - 180px)',
            lg: 'calc(100vh - 133px)',
          },
          pb: { xs: 6, md: 8 },
          mt: '-53px',
        }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
          }}
        />

        {bannerSlides.length > 0 ? (
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              width: '100vw',
              marginLeft: 'calc(50% - 50vw)',
              marginRight: 'calc(50% - 50vw)',
            }}>
            <ImageCarousel slides={bannerSlides} />
          </Box>
        ) : null}

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'center',
            px: { xs: 2, md: 4 },
            pt: { xs: 4, sm: 6, md: 8 },
          }}>
          <Box
            sx={{
              width: '100%',
              maxWidth: 1600,
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 3, md: 4 },
            }}>
            <Box
              sx={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, minmax(340px, 1fr))',
                  lg: `repeat(${navTiles.length}, minmax(260px, 1fr))`,
                },
                gap: { xs: 3, md: 4 },
              }}>
              {navTiles.map(tile => (
                <Card
                  key={tile.key}
                  component={tile.component}
                  to={tile.to}
                  href={tile.href}
                  target={tile.target}
                  rel={tile.rel}
                  onClick={tile.onClick}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    cursor: 'pointer',
                    borderRadius: 4,
                    border: '1px solid rgba(255, 255, 255, 0.18)',
                    bgcolor: '#fff',
                    transition: 'all 0.3s ease',
                    boxShadow: '0',
                    textDecoration: 'none',
                  }}>
                  <CardContent
                    sx={{
                      p: '0!important',
                      flex: 1,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}>
                    <Box
                      sx={{
                        position: 'relative',
                        borderRadius: 0,
                        borderBottomLeftRadius: '8px',
                        borderBottomRightRadius: '8px',
                        border: 'none',
                        outline: 'none',
                        boxShadow: 'none',
                        overflow: 'hidden',
                        height: { xs: 220, sm: 240, md: 300, lg: 330 },
                        backgroundImage: `url(${tile.image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: tile.imagePosition || 'center',
                      }}>
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          background: 'linear-gradient(135deg, rgba(0,0,0,0.55), rgba(0,0,0,0.1))',
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        flex: 1,
                        px: { xs: '14px', md: '16px' },
                        pb: { xs: '14px', md: '16px' },
                        pt: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                      }}>
                      <Box>
                        <Typography variant="h1" sx={{ color: '#000', fontSize: { xs: 24, md: 32 }, textAlign: 'center' }}>
                          {tile.title}
                        </Typography>
                        <Typography variant="h1" sx={{ color: '#000', fontSize: { xs: 24, md: 32 }, textAlign: 'center' }}>
                          {tile.title2}
                        </Typography>
                      </Box>
                      <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'center' }}>
                        <Button
                          component="span"
                          variant="contained"
                          color="secondary"
                          size="large"
                          disableElevation
                          sx={{
                            mt: 4,
                            py: 3,
                            borderRadius: 0,
                            boxShadow: 'none',
                            '&:hover': {
                              backgroundColor: '#EFBA29',
                              boxShadow: 'none',
                            },
                            '&:active': {
                              boxShadow: 'none',
                            },
                            '&.Mui-focusVisible': {
                              boxShadow: 'none',
                            },
                          }}>
                          <Typography
                            variant="h1"
                            sx={{ color: '#000', fontSize: { xs: 16, md: 24 }, letterSpacing: '-0.02em' }}>
                            {tile.description}
                          </Typography>
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
              {!IsClub && (
                <Card
                  component={printOnDemandLinkBehavior.isExternalRoute ? 'a' : Link}
                  to={printOnDemandLinkBehavior.isExternalRoute ? undefined : printOnDemandLinkBehavior.url}
                  href={printOnDemandLinkBehavior.isExternalRoute ? printOnDemandLinkBehavior.url : undefined}
                  target={
                    printOnDemandLinkBehavior.isExternalRoute && printOnDemandLinkBehavior.openInNewTab
                      ? '_blank'
                      : undefined
                  }
                  rel={printOnDemandLinkBehavior.isExternalRoute ? 'noopener noreferrer' : undefined}
                  sx={{
                    gridColumn: { md: '1 / -1' },
                    justifySelf: 'center',
                    width: '100%',
                    maxWidth: 760,
                    borderRadius: 4,
                    border: '1px solid rgba(255, 255, 255, 0.18)',
                    bgcolor: '#fff',
                    backdropFilter: 'blur(18px)',
                    boxShadow: '0',
                    cursor: 'pointer',
                    textDecoration: 'none',
                  }}>
                  <CardContent sx={{ p: { xs: '14px!important', md: '16px!important' } }}>
                    <Stack
                      spacing={1.5}
                      direction={{ xs: 'column', sm: 'row' }}
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                      justifyContent="space-between">
                      <Box>
                        <Typography variant="h1" sx={{ color: '#000', fontWeight: 700, fontSize: { xs: 24, md: 32 } }}>
                          {t('home.printOnDemandTitle')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' } }}>
                        <Button
                          component="span"
                          variant="contained"
                          color="secondary"
                          size="large"
                          disableElevation
                          sx={{
                            p: 3,
                            minWidth: { sm: 220 },
                            lineHeight: 1.1,
                            textAlign: 'center',
                            mt: { xs: 4, sm: 0 },
                            borderRadius: 0,
                            boxShadow: 'none',
                            '&:hover': {
                              backgroundColor: '#EFBA29',
                              boxShadow: 'none',
                            },
                            '&:active': {
                              boxShadow: 'none',
                            },
                            '&.Mui-focusVisible': {
                              boxShadow: 'none',
                            },
                          }}>
                          <Typography
                            variant="h1"
                            sx={{ color: '#000', fontSize: { xs: 16, md: 24 }, letterSpacing: '-0.02em' }}>
                            {t('orderNow')}
                          </Typography>
                        </Button>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Slide>
  );
};

export default HomePage;
